<template>
  <div class="top-up-container">
    <h2 class="title">{{ $t('topUpPage.topUpTitle') }}</h2>
    <promotion-note v-if='topUpPromoMessage' :top-up-promo-message="topUpPromoMessage" />
    <validation-observer ref="topUpObs"
                         tag="form"
                         class="top-up-form"
                         @submit.prevent="submitTopUpForm">
      <base-v-input v-model="inputNumber"
                    id="phoneNumber"
                    name="phoneNumber"
                    container-class="phone-input"
                    :submitted="topUpFormSubmitted"
                    :label="$t('registration.phoneLabel')"
                    rules="required|phoneNumberWithSpaces|max:15"
                    vid="phoneNumber"
                    variant="white"
                    maxlength="15"/>
      <h3 class="sub-title">{{ $t('topUpPage.topUpSubtitle') }}</h3>
      <base-v-input v-model.trim="amount"
                    id="amount"
                    name="amount"
                    container-class="amount"
                    :submitted="topUpFormSubmitted"
                    :label="$t('topUpPage.amount')"
                    :placeholder="topupPlaceholder"
                    :rules="`required|currencyRangeWithComma:${topupValidation.min},${topupValidation.max}|max:5`"
                    vid="amount"
                    variant="white"
                    maxlength="5"
      />
      <base-radio v-model="pickedPayment"
                  size="small"
                  value="cardPayment"
                  name="pickPayment"
                  position="left"
                  vertical-align="start"
                  class="radio-single mobile"
                  :class="{'checked' : isCardPaymentSelected}">
        <p class="payment-info">{{ $t('topUpPage.cardPayment') }}</p>
      </base-radio>
      <base-radio v-model="pickedPayment"
                  size="small"
                  value="accountPayment"
                  name="pickPayment"
                  position="left"
                  vertical-align="start"
                  class="radio-single mobile"
                  :class="{'checked' : isAccountPaymentSelected}">
        <p v-if="$props.resourcesData.subscriptionType === 'MOBILE_PREPAID'" class="payment-info">
          {{ $t('topUpPage.prepaidCheckboxLabel') }}</p>
        <p v-else class="payment-info">{{ $t('topUpPage.accountPayment') }}</p>
      </base-radio>
      <base-button class="top-up-button"
                   variant="primary"
                   :disabled="!formFilled || dataLoading"
                   :loading="dataLoading"
                   @click="validateFields">
        {{ $t('topUpPage.makePayment') }}
      </base-button>
    </validation-observer>

    <confirmation-modal :visible="accountModalVisible"
                        :loading="paymentLoading"
                        :closeButtonVisible=false
                        @close="accountModalVisible = false"
                        :cancel-button="true"
                        :confirm-title="$t('topUpPage.paymentConfirmationTitle')"
                        :dynamic-data-description="$t('topUpPage.paymentConfirmationDescription', { inputNumber: this.inputNumber, amount: this.amount})"
                        @confirm="confirmPayment"/>

    <payment-modal :visible="paymentModalVisible"
                   :loading="paymentLoading"
                   @close="paymentModalVisible = false"
                   @payment-with-saved-card="payWithCard"
                   @payment-with-new-card="payWithNewCard">
      <template slot="title">{{ $t('paymentModal.modalTitle') }}</template>
      <template slot="description">{{ $t('paymentModal.modalDescriptionWithCards', { amount: amount }) }}</template>
      <template slot="noCardDescription">{{ $t('paymentModal.noCardDescription', { phoneNumber: inputNumber, amount: amount }) }}</template>
    </payment-modal>

    <success-modal :visible="successModal"
                   @close="onSuccessModalClose"
                   :successTitle="$t('topUpPage.successModalTitle')"
                   :successDescription="noProvision ? $t('topUpPage.paymentConfirmationNoProvision', { inputNumber: this.inputNumber, amount: this.amount}) : $t('topUpPage.paymentConfirmationDescription', { inputNumber: this.inputNumber, amount: this.amount})"/>

    <bank-form :form-data="bankAuthForm" ref="bankForm"/>
  </div>
</template>

<script>
import SuccessModal from '@/components/common/modal/success-modal'
import ConfirmationModal from '@/components/common/modal/confirmation-modal'
import errorMessageHandler from '@/mixins/errorMessageHandler'
import PaymentModal from '@/components/common/payment-cards/payment-modal'
import BankForm from '@/components/common/payment-cards/bank-form'
import selectionBlockEvents from '@/mixins/selectionBlockEvents'
import { addDataLayerEvent } from '@/services/dataLayer'
import i18n from '@/i18n'
import PromotionNote from '../../common/promotion-note.vue'

export default {
  name: 'postpaid-top-up',
  components: { PromotionNote, BankForm, PaymentModal, SuccessModal, ConfirmationModal },
  data () {
    return {
      accountModalVisible: false,
      inputNumber: '',
      amount: '',
      dataLoading: false,
      pickedPayment: '',
      topUpFormSubmitted: false,
      successModal: false,
      payedAmount: '',
      paymentModalVisible: false,
      paymentLoading: false,
      bankAuthForm: {},
      noProvision: false,
      topupPlaceholder: ''
    }
  },
  mixins: [errorMessageHandler, selectionBlockEvents],
  props: {
    resourcesData: {
      type: Object,
      default: () => {
        return {}
      },
      required: true
    },
    topUpPromoMessage: {
      type: String,
      default: ''
    }
  },
  computed: {
    formFilled () {
      return !!this.inputNumber && !!this.amount && !!this.pickedPayment
    },
    topupValidation () {
      return this.isCardPaymentSelected ? { min: 3, max: 50 } : { min: 3, max: 20 }
    },
    isCardPaymentSelected () {
      return this.pickedPayment === 'cardPayment'
    },
    isAccountPaymentSelected () {
      return this.pickedPayment === 'accountPayment'
    }
  },
  watch: {
    pickedPayment: {
      handler () {
        this.topupPlaceholder = i18n.t('topUpPage.topUpAmountPlaceholder', { min: this.topupValidation.min, max: this.topupValidation.max })
      }
    }
  },
  methods: {
    payWithCard (cardCode) {
      this.paymentLoading = true
      const dataToSend = {
        amount: this.amount.replace(',', '.'),
        cardCode: cardCode,
        toMsisdn: this.inputNumber
      }
      this.$apiRequest.post('user/mobileservice/processpayment', dataToSend)
        .then(response => {
          if (response.data.status === 'FAILED') {
            this.toast('error', {
              message: this.$t('toastMessages.paymentFail')
            })
          } else if (response.data.status === 'SOFT_DECLINE') {
            this.bankAuthForm = response.data.bankFormStartParameters
            setTimeout(() => {
              this.$refs.bankForm.submitForm()
            }, 50)
          } else {
            this.paymentModalVisible = false
            this.noProvision = true
            this.successModal = true
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.paymentLoading = false
        })
    },
    payWithNewCard (options) {
      const { remember } = options || false
      const dataToSend = {
        amount: this.amount.replace(',', '.'),
        saveCard: remember,
        toMsisdn: this.inputNumber
      }
      this.paymentLoading = true
      this.$apiRequest.post('user/mobileservice/authform', dataToSend)
        .then(response => {
          this.bankAuthForm = response.data
          this.noProvision = true

          setTimeout(() => {
            this.$refs.bankForm.submitForm()
          }, 50)
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
          this.paymentLoading = false
        })
    },
    openModal () {
      if (this.pickedPayment === 'accountPayment') {
        this.accountModalVisible = true
      } else if (this.pickedPayment === 'cardPayment') {
        this.paymentModalVisible = true
      }
    },
    confirmPayment () {
      this.submitTopUpForm()
    },
    resetData () {
      this.inputNumber = ''
      this.amount = ''
      this.payedAmount = ''
      this.pickedPayment = ''
      this.accountModalVisible = false
      this.topUpFormSubmitted = false
      this.paymentLoading = false
      this.noProvision = false
    },
    onSuccessModalClose () {
      this.successModal = false
      this.resetData()
      this.sendRefreshDataEvent()
    },
    async validateFields () {
      this.topUpFormSubmitted = true
      const valid = await this.$refs.topUpObs.validate()
      if (valid) {
        this.openModal()
      }
    },
    async submitTopUpForm () {
      this.paymentLoading = true
      this.topUpFormSubmitted = true
      const normalizedNumber = this.inputNumber.replace(/[^0-9]/g, '')

      this.$apiRequest.post('user/mobileservice/topup', {
        fromMsisdn: this.$props.resourcesData.subscriberIdentity,
        toMsisdn: normalizedNumber,
        amount: this.amount.replace(',', '.')
      })
        .then(response => {
          if (response.status === 200) {
            this.accountModalVisible = false
            this.successModal = true
            this.payedAmount = this.amount
            addDataLayerEvent({
              amount: this.amount,
              type: this.pickedPayment === 'accountPayment' ? 'bill' : 'card',
              event: 'prepaid_topup'
            })
          }
        })
        .catch(e => {
          this.handleErrorMessage(e.response.data)
        })
        .finally(() => {
          this.paymentLoading = false
        })
    },
    sendRefreshDataEvent () {
      if (this.$props.resourcesData.subscriberIdentity) {
        this.$emit('refresh-data', this.$props.resourcesData.subscriberIdentity)
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.unblockSidebarSelection(false)
    })
  }
}
</script>
<style scoped lang="scss">
.top-up-container {
  padding-left: 0.5rem;

  .title {
    font-size: 2.4rem;
    line-height: 2.4rem;
    color: $light-black;
    text-transform: uppercase;
    padding-bottom: 1rem;
    margin-top: 2rem;
    font-weight: normal;
    border-bottom: 1px solid $form-border-color;
  }

  .top-up-form {
    max-width: 36rem;
    margin-top: 2.4rem;
    margin-left: 0.5rem;

    .sub-title {
      font-size: 1.6rem;
      line-height: 2rem;
      color: $light-black;
      margin-bottom: 1.8rem;
      margin-top: 0.2rem;
      font-weight: normal;
    }
  }

  .payment-info {
    font-size: 1.2rem;
    font-weight: 600;
    color: $font-inactive;
    margin-right: 1.4rem;
    margin-left: 0.6rem;
  }

  .description {
    font-size: 1.4rem;
    color: $light-black;
    margin-bottom: 0;

    @media (map_get($breakpoints, sm)) {
      font-size: 1.6rem;
      margin-bottom: 4rem;
    }
  }

  .radio-single {
    width: 100%;
    margin-bottom: 0;

    @media (map_get($breakpoints, sm)) {
      width: auto;
    }

    &.small-checkbox {
      width: 30px;

      @media (map_get($breakpoints, sm)) {
        width: auto;
      }
    }

    &.mobile {
      margin-bottom: 1.5rem;
    }

    &.checked {
      .payment-info {
        color: $light-black;
      }
    }
  }

  .top-up-button {
    width: 100%;

    @media (map_get($breakpoints, sm)) {
      width: auto;
      margin-top: 1rem;
    }
  }
}

.credit-card-wrapper {
  display: inline-flex;
  align-items: center;

  span {
    padding: 0 2rem;
  }
}

.payment-modal-header {
  text-align: center;
  margin-bottom: 4rem;
  margin-top: 4rem;

  .payment-modal-title {
    font-size: 2rem;
    line-height: 2.6rem;
    color: $light-black;
    font-weight: normal;

    @media (map_get($breakpoints, sm)) {
      font-size: 3.2rem;
    }
  }
}

.payment-body {
  text-align: center;

  &.account-payment {
    padding-bottom: 4rem;
    position: relative;

    .button-container {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      width: 100%;

      @media (map_get($breakpoints, sm)) {
        flex-direction: row;
      }

      .base-button {
        width: 100%;
        margin-top: 4rem;

        @media (map_get($breakpoints, sm)) {
          margin-right: 2.4rem;
          margin-top: 0;
          width: auto;
        }
      }
    }

    .cancel-payment-btn {
      color: $primary;
      margin-left: 0;
      font-size: 1.4rem;
      margin-top: 1rem;

      @media (map_get($breakpoints, sm)) {
        margin-left: 4rem;
        font-size: 1.6rem;
        margin-top: 0;
      }
    }
  }
}

.first-radio {
  margin-bottom: 2.6rem;
}

.single-radio-wrapper {
  .radio-single {
    width: 100%;
    padding: 1.6rem;
  }

  label {
    margin-bottom: 0;
    font-size: 1.4rem;
    font-weight: 600;
    color: $checkbox-label;
  }
}

.old-card.mobile {
  width: 100%;
}
</style>
